// Common
import DataTable from "@components/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  faTrashCan,
  faSpinner,
  faCheck,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AdminListCard, ConfirmDeleteModal } from "@components/CatalogIndex";
import type { BatchAction } from "@components/CatalogIndex";
import { useV2DeleteOpenRouterKeyMutation } from "@generated";
import { useAuthorization } from "../../../../common/providers/AuthorizationProvider";
// OpenRouterKey Specific
import useListOpenRouterKeys from "./api";
import { OpenRouterKey } from "./types";
import { KEY_TYPE_OPTIONS } from "../common/types";

const List = (): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const authorized = useAuthorization();
  const [deleteOpenRouterKey, {}] = useV2DeleteOpenRouterKeyMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { openRouterKeys, pageMeta, isLoading, refetch } =
    useListOpenRouterKeys({
      page: {
        number: pageNumber,
        size: pageSize,
      },
    });

  useEffect(() => {
    if ((location.state as any)?.refetch) {
      refetch();
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const handleConfirm = async () => {
    await Promise.all(
      selectedIds.map((keyId) => deleteOpenRouterKey({ id: keyId }).unwrap())
    );
    setSelectedIds([]);
    refetch();
    setShowConfirmationModal(false);
  };

  const getColumns = (): ColumnDef<OpenRouterKey>[] => {
    if (!authorized || isLoading) {
      return [
        {
          header: "Name",
          cell(props) {
            return <FontAwesomeIcon icon={faSpinner} spin />;
          },
        },
        {
          header: "Key Type",
          cell(props) {
            return <FontAwesomeIcon icon={faSpinner} spin />;
          },
        },
      ];
    }

    if (openRouterKeys.length === 0) {
      return [
        {
          header: "Key Type",
          id: "empty",
          cell() {
            return <div>No Private LLM Keys configured</div>;
          },
        },
      ];
    }

    return [
      {
        header: "Name",
        cell(props) {
          return (
            <div
              className="openRouterKeyName"
              onClick={() =>
                navigate(
                  `/admin/open_router_keys/${props.row.original.id}/edit`
                )
              }
            >
              {props.row.original.name}
            </div>
          );
        },
      },
      {
        header: "Key Type",
        cell(props) {
          return (
            <div>
              {KEY_TYPE_OPTIONS.find(
                (opt) => opt.value === props.row.original.keyType
              )?.label || props.row.original.keyType}
            </div>
          );
        },
      },
      {
        header: "Default",
        cell(props) {
          return (
            <div>
              {props.row.original.default && <FontAwesomeIcon icon={faCheck} />}
            </div>
          );
        },
      },
    ];
  };

  const columns = getColumns();
  const hasData = !!authorized && !isLoading && openRouterKeys.length > 0;

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: () => setShowConfirmationModal(true),
      variant: "danger",
    },
  ];

  return (
    <>
      <AdminListCard
        title="Private LLM Keys"
        onNew={() => navigate("/admin/open_router_keys/new")}
        newButtonLabel="Add A Private LLM Key"
        batchActions={batchActions}
        selectedCount={selectedIds.length}
      >
        <DataTable
          data={hasData ? openRouterKeys : [{} as OpenRouterKey]}
          columns={columns}
          selectable={hasData}
          bordered
          striped
          hover
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={setPageNumber}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          paginationEnabled={false}
          pageSizeEnabled={false}
        />
      </AdminListCard>
      <ConfirmDeleteModal
        open={showConfirmationModal}
        title="Confirm Delete"
        message={`Are you sure that you want to delete ${selectedIds.length} private LLM keys?`}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirmationModal(false)}
      />
    </>
  );
};

export default List;
